import React from 'react';
import './../styles/global.css';
import './../util/analytics';
import 'maplibre-gl/dist/maplibre-gl.css';
import Navbar from './../components/Navbar';
import IndexPage from './index';
import FeedPage from './feed';
import AboutPage from './about';
import PricingPage from './pricing';
import AuthPage from './auth';
import AdminHomePage from './admin-home';
import AdminLoginPage from './admin-login';
import AdminApplyPage from './admin-apply';
import ProfilePage from './profile';
import DashboardPage from './dashboard';
import PreviewPage from './preview';
import MyListsPage from './my-lists';
import SingleListPage from './SingleListPage';
import SettingsPage from './settings';
import EmbedMap from './embed-map';
import LegalPage from './legal';
import FutureFair2026Page from './future-fair-2026';
import { Navigate, Routes, Route, Router, useRouter } from './../util/router';
import AnalyticsPageViews from './../components/AnalyticsPageViews';
import PurchasePage from './purchase';
import NotFoundPage from './404';
import { AuthProvider, useAuth } from './../util/auth';
import { QueryClientProvider } from './../util/db';
import ShowPage from './show/ShowPage';
import SeenShowsPage from './SeenShowsPage';
import MapPage from './map';
import OrgPage from './org';
import SearchPage from './search';
import FeaturedPage from './featured';
import WantToSeePage from './want-to-see';
import FollowingShowsPage from './following-shows';
import FollowingOrgsPage from './following';
import AppShell from './../components/shell/AppShell';
import { ToastProvider } from './../components/ui/Toast';
import { useUserShowsStore } from './../stores';

// External redirect component for app store links
function ExternalRedirect({ to }) {
  React.useEffect(() => {
    window.location.href = to;
  }, [to]);
  return null;
}

const STANDALONE_PREFIXES = [
  '/auth',
  '/admin',
  '/embed-map',
  '/preview',
  '/pricing',
  '/purchase',
  '/future-fair-2026',
];

export function isStandaloneRoute(pathname) {
  return pathname === '/' || STANDALONE_PREFIXES.some(prefix => pathname.startsWith(prefix));
}

function AppContent() {
  const { pathname } = useRouter();
  const auth = useAuth();
  const fetchUserShows = useUserShowsStore(s => s.fetchUserShows);
  const userId = auth.user?.id;

  React.useEffect(() => {
    fetchUserShows(userId);
  }, [userId, fetchUserShows]);

  const standalone = isStandaloneRoute(pathname);

  const routes = (
      <Routes>
        <Route path="/" element={<IndexPage />} />
        <Route path="/feed" element={<FeedPage />} />
        <Route path="/map" element={<MapPage />} />
        <Route path="/org/:id" element={<OrgPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/featured" element={<FeaturedPage />} />
        <Route path="/want-to-see/:userId" element={<WantToSeePage />} />
        <Route path="/following-shows/:userId" element={<FollowingShowsPage />} />
        <Route path="/following/:userId" element={<FollowingOrgsPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/fairs/*" element={<Navigate to="/feed" replace />} />
        <Route path="/profile/:username" element={<ProfilePage />} />
        <Route path="/seen-shows/:userId" element={<SeenShowsPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/auth/:type" element={<AuthPage />} />
        <Route path="/admin" element={<AdminHomePage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin/apply" element={<AdminApplyPage />} />
        <Route path="/admin/dashboard" element={<DashboardPage />} />
        <Route path="/settings/:section" element={<SettingsPage />} />
        <Route path="/my-lists" element={<MyListsPage />} />
        <Route path="/lists/:id" element={<SingleListPage />} />
        <Route path="/legal/:section" element={<LegalPage />} />
        <Route path="/purchase/:plan" element={<PurchasePage />} />
        <Route path="/show/:id" element={<ShowPage />} />
        <Route path="/preview/:id" element={<PreviewPage />} />
        <Route path="/embed-map" element={<EmbedMap />} />
        <Route path="/future-fair-2026" element={<FutureFair2026Page />} />
        <Route
          path="/download-ios"
          element={
            <ExternalRedirect to="https://apps.apple.com/us/app/showrunner-app/id6745201078" />
          }
        />
        <Route
          path="/download-android"
          element={
            <ExternalRedirect to="https://play.google.com/store/apps/details?id=com.sahirgomez.showrunnermobile&pcampaignid=web_share&fbclid=PAZXh0bgNhZW0CMTEAAaeubZWe4xZ_w6Sn_rANJ9EvDpdrWdmWsG2b41spZnQtz-FfVilEmAiN63G2xQ_aem_RXeDkI_dlJcc_pFw2fHs-Q&pli=1" />
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
  );

  return (
    <ToastProvider>
      {standalone ? (
        <>
          <Navbar isEmbed={pathname === '/embed-map'} />
          {routes}
        </>
      ) : (
        <AppShell>{routes}</AppShell>
      )}
    </ToastProvider>
  );
}

function App(props) {
  return (
    <QueryClientProvider>
      <AuthProvider>
        <Router>
          <AnalyticsPageViews />
          <AppContent />
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
